import { TimeSlotGroup } from 'timeSlots/timeSlotsService'
import {
    applicableGroupContexts,
    CategoryTimeSlotInfo,
    contextKey,
    distinctWeekdays,
    groupHasDates,
    groupIsLimited,
    GroupScope,
    ProductGroupsInput,
} from 'timeSlots/domain/applicableGroups'
import { CountingType } from 'capacity/types'

export type TakesUpKind = 'slotEnd' | 'slotStart' | 'dayEnd' | 'dayStart' | 'dependsOnCategory' | 'dependsOnDate'

export interface GroupSource {
    groupId: string
    groupName: string
    scope: GroupScope
    categoryId?: string
    categoryName?: string
    limited: boolean
}

export interface TakesUp {
    kind: TakesUpKind
    sources: GroupSource[]
}

export type TakesUpState = TakesUp | 'pending' | 'unavailable' | null

export const isResolved = (state: TakesUpState): state is TakesUp => !!state && typeof state === 'object'

const ALL_WEEKDAYS_COUNT = 7

const groupsDependOnDate = (groups: TimeSlotGroup[]): boolean => {
    if (groups.some((group) => !groupIsLimited(group))) {
        return false
    }
    if (groups.some(groupHasDates)) {
        return true
    }
    return new Set(groups.flatMap(distinctWeekdays)).size < ALL_WEEKDAYS_COUNT
}

const knownSlotsAllLackEnd = (groups: TimeSlotGroup[]): boolean =>
    groups.every((group) => Array.isArray(group.timeSlots) && group.timeSlots.every((slot) => !slot.endTime))

export function takesUpCapacity(
    product: ProductGroupsInput,
    timeSlotGroups: TimeSlotGroup[],
    countingType: CountingType,
    categories: CategoryTimeSlotInfo[] = [],
): TakesUp {
    const contexts = applicableGroupContexts(product, timeSlotGroups, categories)
    const groups: TimeSlotGroup[] = []
    const sources: GroupSource[] = []
    for (const context of contexts) {
        for (const group of context.groups) {
            if (groups.some((entry) => entry.id === group.id)) {
                continue
            }
            groups.push(group)
            sources.push({
                groupId: group.id,
                groupName: group.name,
                scope: context.scope,
                ...(context.scope === 'category'
                    ? { categoryId: context.categoryId, categoryName: context.categoryName }
                    : {}),
                limited: groupIsLimited(group),
            })
        }
    }
    const arrival = countingType === 'arrivalOnly'
    if (groups.length === 0) {
        return { kind: arrival ? 'dayStart' : 'dayEnd', sources }
    }
    if (new Set(contexts.map(contextKey)).size > 1) {
        return { kind: 'dependsOnCategory', sources }
    }
    if (groupsDependOnDate(groups)) {
        return { kind: 'dependsOnDate', sources }
    }
    if (!arrival && knownSlotsAllLackEnd(groups)) {
        return { kind: 'slotStart', sources }
    }
    return { kind: arrival ? 'slotStart' : 'slotEnd', sources }
}

export const TAKES_UP_SORT_ORDER: TakesUpKind[] = [
    'slotEnd',
    'slotStart',
    'dependsOnDate',
    'dependsOnCategory',
    'dayEnd',
    'dayStart',
]
